import type { OptionValue } from "@tailor-frame/engine";

/**
 * A recipe is how one pattern is made: the tasks in the order they are sewn,
 * some fixed and some chosen in design slots (front pockets: none, side,
 * slant or western). A slot's choice may set the pattern's own options, so
 * the draft and the making stay the same garment, and may need options other
 * choices set (an inside lining pocket needs a lining).
 *
 * A project follows a recipe in stages: draft, toile (optional, repeated
 * until it fits), cut, make and finish. Tasks marked `fit` are the seams
 * that shape the garment, tacked together for the toile.
 */

export type Stage = "draft" | "toile" | "cut" | "make" | "finish";

export const stageLabels: Record<Stage, string> = { draft: "Draft", toile: "Toile", cut: "Cut", make: "Make", finish: "Finish" };

export interface RecipeTask {
  id: string;
  title: string;
  /** The skill it uses, by skill id. */
  skill?: string;
  /** How many are made, such as a pocket on each leg. */
  count?: number;
  /** Tasks or slots done before this one; ids not in the plan (a slot left empty) are passed over. */
  after?: string[];
  /** A seam that shapes the fit, tacked together for the toile. */
  fit?: boolean;
  /** Finishing tasks come after the making. Default "make". */
  stage?: "make" | "finish";
}

export interface SlotChoice {
  id: string;
  label: string;
  /** Pattern options this design sets, such as a two-piece leg. */
  options?: Record<string, OptionValue>;
  /** Pattern options this design needs, set by the recipe or by other slots, each with the values that will do. */
  requires?: Record<string, OptionValue[]>;
  tasks?: RecipeTask[];
}

export interface RecipeSlot {
  id: string;
  label: string;
  default: string;
  choices: SlotChoice[];
}

export interface Recipe {
  id: string;
  patternId: string;
  name: string;
  summary: string;
  /** Fitting checks for the toile. */
  fitChecks: FitCheckDefinition[];
  /** Whether a new project makes a toile first. */
  toile: boolean;
  /** Pattern options the recipe always drafts with. */
  options?: Record<string, OptionValue>;
  slots: RecipeSlot[];
  tasks: RecipeTask[];
}

/** A fitting check, and the pattern measurements a change to it can adjust (those the pattern has). */
export interface FitCheckDefinition { label: string; measurements?: string[] }

const trouserChecks: FitCheckDefinition[] = [
  { label: "Waist sits level and fits without gaping", measurements: ["trouserWaist"] },
  { label: "Seat is smooth, not tight or baggy", measurements: ["seat"] },
  { label: "Rise and crutch are comfortable sitting down", measurements: ["bodyRise"] },
  { label: "Side seams hang straight" },
  { label: "Legs hang without twisting" },
  { label: "Leg width at the hem", measurements: ["trouserBottomWidth"] },
  { label: "Length breaks on the shoe as wanted", measurements: ["insideLeg"] },
];
const jacketChecks: FitCheckDefinition[] = [
  { label: "Chest and waist close without pulling", measurements: ["chest"] },
  { label: "Back is smooth across the blades", measurements: ["halfBack"] },
  { label: "Back balance: hem level, no folds under the collar", measurements: ["naturalWaistLength"] },
  { label: "Collar sits on the neck", measurements: ["neck"] },
  { label: "Armhole is not tight or too low", measurements: ["scyeDepth"] },
  { label: "Sleeve pitch: no twist or drag lines" },
  { label: "Sleeve length", measurements: ["twoPieceSleeveLength"] },
  { label: "Jacket length", measurements: ["jacketLength"] },
];

const frontPocketChoices: SlotChoice[] = [
  { id: "none", label: "No front pockets" },
  { id: "side", label: "On-seam side pockets", tasks: [{ id: "front-pockets", title: "Side pockets", skill: "side-pocket", count: 2 }] },
  { id: "slant", label: "Slant pockets", tasks: [{ id: "front-pockets", title: "Slant pockets", skill: "slant-pocket", count: 2 }] },
  { id: "western", label: "Western pockets", tasks: [{ id: "front-pockets", title: "Western pockets", skill: "western-pocket", count: 2 }] },
];

export const recipes: Recipe[] = [
  {
    id: "flat-trousers",
    patternId: "flat-trousers",
    name: "Flat trousers",
    summary: "Casual trousers on the flat block, with a choice of front pockets and a zip fly or a pull-on waist.",
    fitChecks: trouserChecks,
    toile: true,
    slots: [
      { id: "legs", label: "Legs", default: "twoPiece", choices: [
        { id: "twoPiece", label: "Two-piece, with side seams", options: { pieces: "twoPiece" }, tasks: [{ id: "side-seams", title: "Side seams", skill: "plain-seam", after: ["front-pockets"], fit: true }] },
        { id: "onePiece", label: "One-piece, no side seam", options: { pieces: "onePiece" } },
      ] },
      // The front pockets are made on the side seam, so a one-piece leg has none.
      { id: "front-pockets", label: "Front pockets", default: "slant", choices: frontPocketChoices.map((choice) => choice.id === "none" ? choice : { ...choice, requires: { pieces: ["twoPiece"] } }) },
      { id: "opening", label: "Opening", default: "zip", choices: [
        { id: "zip", label: "Zip fly and waistband", tasks: [{ id: "fly", title: "Zip fly", skill: "zip-fly", after: ["front-pockets"] }, { id: "waist", title: "Waistband", skill: "waistband", after: ["side-seams", "seat-seam"], fit: true }] },
        { id: "pullOn", label: "Pull-on, elastic casing", tasks: [{ id: "waist", title: "Waist casing", skill: "casing", after: ["side-seams", "seat-seam"], fit: true }] },
      ] },
    ],
    tasks: [
      { id: "inside-leg", title: "Inside leg seams", skill: "plain-seam", after: ["side-seams"], fit: true },
      { id: "seat-seam", title: "Seat seam", skill: "seat-seam", after: ["inside-leg", "fly"], fit: true },
      { id: "hems", title: "Hems", skill: "hem", after: ["inside-leg"], stage: "finish" },
      { id: "press", title: "Final press", skill: "pressing", after: ["hems", "waist"], stage: "finish" },
    ],
  },
  {
    id: "classic-trousers",
    patternId: "classic-trousers",
    name: "Classic trousers",
    summary: "Tailored trousers in four styles, with piped back pockets, a zip fly and a choice of waistband.",
    fitChecks: trouserChecks,
    toile: true,
    slots: [
      { id: "style", label: "Style", default: "basic", choices: [
        { id: "basic", label: "Basic", options: { trouserStyle: "basic" } },
        { id: "pleated", label: "Pleated waistline", options: { trouserStyle: "pleated" }, tasks: [{ id: "pleats", title: "Front pleats", skill: "pleats", fit: true }] },
        { id: "extraFullness", label: "Extra fullness, three pleats", options: { trouserStyle: "extraFullness" }, tasks: [{ id: "pleats", title: "Front pleats", skill: "pleats", fit: true }] },
        { id: "slimline", label: "Slimline", options: { trouserStyle: "slimline" } },
      ] },
      { id: "front-pockets", label: "Front pockets", default: "side", choices: frontPocketChoices.filter((choice) => choice.id !== "western") },
      { id: "back-pockets", label: "Back pockets", default: "one", choices: [
        { id: "none", label: "No back pockets" },
        { id: "one", label: "One, on the right", tasks: [{ id: "back-pockets", title: "Back pocket", skill: "back-pocket", after: ["back-darts"] }] },
        { id: "two", label: "Two", tasks: [{ id: "back-pockets", title: "Back pockets", skill: "back-pocket", count: 2, after: ["back-darts"] }] },
      ] },
      { id: "waistband", label: "Waistband", default: "basic", choices: [
        { id: "basic", label: "Two-piece band", options: { waistbandStyle: "basic" }, tasks: [{ id: "waist", title: "Waistband", skill: "waistband", after: ["side-seams", "seat-seam"], fit: true }] },
        { id: "slideAdjusters", label: "With slide adjusters", options: { waistbandStyle: "slideAdjusters" }, tasks: [{ id: "waist", title: "Waistband with slide adjusters", skill: "waistband", after: ["side-seams", "seat-seam"], fit: true }] },
        { id: "doubleCloth", label: "Double cloth", options: { waistbandStyle: "doubleCloth" }, tasks: [{ id: "waist", title: "Double cloth waistband", skill: "waistband", after: ["side-seams", "seat-seam"], fit: true }] },
        { id: "casing", label: "Casing for a tie or belt", options: { waistbandStyle: "casing" }, tasks: [{ id: "waist", title: "Waist casing", skill: "casing", after: ["side-seams", "seat-seam"], fit: true }] },
      ] },
    ],
    tasks: [
      { id: "back-darts", title: "Back darts", skill: "darts", fit: true },
      { id: "fly", title: "Zip fly", skill: "zip-fly", after: ["front-pockets", "pleats"] },
      { id: "side-seams", title: "Side seams", skill: "plain-seam", after: ["front-pockets", "back-pockets"], fit: true },
      { id: "inside-leg", title: "Inside leg seams", skill: "plain-seam", after: ["side-seams"], fit: true },
      { id: "seat-seam", title: "Seat seam", skill: "seat-seam", after: ["inside-leg", "fly"], fit: true },
      { id: "hems", title: "Hems", skill: "hem", after: ["inside-leg"], stage: "finish" },
      { id: "press", title: "Final press", skill: "pressing", after: ["hems", "waist"], stage: "finish" },
    ],
  },
  {
    id: "classic-suit-jacket",
    patternId: "classic-suit-jacket",
    name: "Classic suit jacket",
    summary: "A tailored jacket on a canvas front, single or double breasted, with a choice of pockets and lining.",
    fitChecks: jacketChecks,
    toile: true,
    slots: [
      { id: "design", label: "Front", default: "block", choices: [
        { id: "block", label: "Single breasted", options: { design: "block" } },
        { id: "doubleBreasted", label: "Double breasted", options: { design: "doubleBreasted" } },
      ] },
      { id: "lining", label: "Lining", default: "full", choices: [
        { id: "full", label: "Fully lined", options: { lining: "full" }, tasks: [{ id: "lining", title: "Make and fell in the lining", skill: "lining", after: ["sleeves", "inside-pocket"] }] },
        { id: "none", label: "Unlined", options: { lining: "none" } },
      ] },
      { id: "breast-pocket", label: "Breast pocket", default: "welt", choices: [
        { id: "welt", label: "Welt pocket", tasks: [{ id: "breast-pocket", title: "Breast welt pocket", skill: "welt-pocket", after: ["darts"] }] },
        { id: "none", label: "No breast pocket" },
      ] },
      { id: "hip-pockets", label: "Hip pockets", default: "flap", choices: [
        { id: "piped", label: "Double-piped", tasks: [{ id: "hip-pockets", title: "Double-piping pockets", skill: "double-piping-pocket", count: 2, after: ["side-body"] }] },
        { id: "flap", label: "Double-piped with flaps", tasks: [{ id: "hip-pockets", title: "Double-piping pockets with flaps", skill: "double-piping-pocket", count: 2, after: ["side-body"] }] },
        { id: "patch", label: "Patch pockets", tasks: [{ id: "hip-pockets", title: "Patch pockets", skill: "patch-pocket", count: 2, after: ["side-body"] }] },
        { id: "none", label: "No hip pockets" },
      ] },
      { id: "inside-pocket", label: "Inside pocket", default: "barcelona", choices: [
        { id: "barcelona", label: "Barcelona lining pocket", requires: { lining: ["full"] }, tasks: [{ id: "inside-pocket", title: "Barcelona lining pocket", skill: "barcelona-pocket" }] },
        { id: "none", label: "No inside pocket" },
      ] },
    ],
    tasks: [
      { id: "darts", title: "Front darts", skill: "darts", fit: true },
      { id: "side-body", title: "Join the side body", skill: "plain-seam", after: ["darts"], fit: true },
      { id: "canvas", title: "Canvas the fronts", skill: "jacket-canvas", after: ["hip-pockets", "breast-pocket"] },
      { id: "back", title: "Centre back and side seams", skill: "plain-seam", after: ["side-body"], fit: true },
      { id: "shoulders", title: "Shoulder seams", skill: "plain-seam", after: ["canvas", "back"], fit: true },
      { id: "collar", title: "Collar and lapels", skill: "collar", after: ["shoulders"], fit: true },
      { id: "sleeves", title: "Make and set the sleeves", skill: "set-in-sleeve", after: ["shoulders"], fit: true },
      { id: "buttonholes", title: "Buttonholes and buttons", skill: "buttonholes", after: ["collar", "lining"], stage: "finish" },
      { id: "press", title: "Final press", skill: "pressing", after: ["buttonholes"], stage: "finish" },
    ],
  },
];

export const recipeById = (id: string | undefined) => recipes.find((recipe) => recipe.id === id);

export const defaultChoices = (recipe: Recipe): Record<string, string> => Object.fromEntries(recipe.slots.map((slot) => [slot.id, slot.default]));

/** The pattern options the recipe drafts with, given its choices. */
export function optionsFor(recipe: Recipe, choices: Record<string, string>): Record<string, OptionValue> {
  const options: Record<string, OptionValue> = { ...recipe.options };
  for (const slot of recipe.slots) Object.assign(options, choiceFor(slot, choices[slot.id])?.options);
  return options;
}

export const choiceFor = (slot: RecipeSlot, id: string | undefined) => slot.choices.find((choice) => choice.id === id) ?? slot.choices.find((choice) => choice.id === slot.default);

/** Why a choice cannot go with the other choices, or undefined when it can. */
export function unavailableReason(recipe: Recipe, slot: RecipeSlot, choice: SlotChoice, choices: Record<string, string>): string | undefined {
  if (!choice.requires) return undefined;
  // The other slots' choices, with this one in its slot.
  const options = optionsFor(recipe, { ...choices, [slot.id]: choice.id });
  for (const [option, allowed] of Object.entries(choice.requires)) {
    if (allowed.includes(options[option])) continue;
    const setter = recipe.slots.find((other) => other.choices.some((item) => item.options?.[option] !== undefined && allowed.includes(item.options[option])));
    const needed = setter?.choices.find((item) => item.options?.[option] !== undefined && allowed.includes(item.options[option]));
    return setter && needed ? `Needs ${setter.label.toLowerCase()}: ${needed.label.toLowerCase()}` : `Needs ${option} ${allowed.join(" or ")}`;
  }
  return undefined;
}

/** Choices made valid: an unknown or unavailable choice falls back to the slot's default, then to the first that will do. */
export function settleChoices(recipe: Recipe, wanted: Record<string, string>): Record<string, string> {
  const choices = { ...defaultChoices(recipe), ...wanted };
  for (const slot of recipe.slots) {
    if (!slot.choices.some((choice) => choice.id === choices[slot.id])) choices[slot.id] = slot.default;
    const current = choiceFor(slot, choices[slot.id])!;
    if (unavailableReason(recipe, slot, current, choices)) choices[slot.id] = slot.choices.find((choice) => !unavailableReason(recipe, slot, choice, choices))?.id ?? slot.default;
  }
  return choices;
}

export interface PlanStep {
  id: string;
  stage: Stage;
  title: string;
  skill?: string;
  count?: number;
  /** The design slot the step comes from, so the slot locks once it is started. */
  slot?: string;
  /** What the step opens: the drafted pattern, its print, or the toile's fitting checks. */
  action?: "draft" | "print" | "fitting";
  /** The toile round it belongs to, from 1. */
  round?: number;
}

/** The recipe's sewing tasks for these choices, each sewn after the tasks it follows. */
export function sewingTasks(recipe: Recipe, choices: Record<string, string>): (RecipeTask & { slot?: string })[] {
  const listed: (RecipeTask & { slot?: string })[] = [];
  // Slot tasks go before the fixed ones, so with nothing between them a slot's tasks come up first.
  for (const slot of recipe.slots) for (const task of choiceFor(slot, choices[slot.id])?.tasks ?? []) listed.push({ ...task, slot: slot.id });
  listed.push(...recipe.tasks);
  const ids = new Set(listed.map((task) => task.id));
  const sorted: typeof listed = [];
  const placed = new Set<string>();
  // Each round places the first listed task whose earlier tasks are all placed; a loop is broken in list order.
  while (sorted.length < listed.length) {
    const next = listed.find((task) => !placed.has(task.id) && (task.after ?? []).every((id) => !ids.has(id) || placed.has(id))) ?? listed.find((task) => !placed.has(task.id))!;
    placed.add(next.id);
    sorted.push(next);
  }
  return sorted;
}

/** Every step of a project, from drafting to the final press, with a toile round for each fitting made. */
export function planFor(recipe: Recipe, choices: Record<string, string>, toile: { on: boolean; rounds: number }): PlanStep[] {
  const tasks = sewingTasks(recipe, choices);
  const steps: PlanStep[] = [
    { id: "draft", stage: "draft", title: "Draft the pattern", action: "draft" },
    { id: "print", stage: "draft", title: "Print and cut out the pattern", action: "print" },
  ];
  if (toile.on) {
    const fitSeams = tasks.filter((task) => task.fit).map((task) => task.title.toLowerCase());
    for (let round = 1; round <= Math.max(1, toile.rounds); round += 1) {
      const name = toile.rounds > 1 ? `toile ${round}` : "the toile";
      steps.push(
        { id: `toile-${round}-cut`, stage: "toile", round, title: `Cut ${name} in calico`, skill: "toile" },
        { id: `toile-${round}-tack`, stage: "toile", round, title: `Tack it together: ${fitSeams.join(", ")}`, skill: "tacking" },
        { id: `toile-${round}-fit`, stage: "toile", round, title: `Try on ${name} and check the fit`, skill: "fitting", action: "fitting" },
      );
    }
  }
  steps.push({ id: "cut", stage: "cut", title: "Lay out and cut the cloth" });
  for (const task of tasks) steps.push({ id: task.id, stage: task.stage ?? "make", title: task.title, skill: task.skill, count: task.count, slot: task.slot });
  return steps;
}
